import { updateCache } from "@/services/backend/_lib/update-cache";
import { DocumentState } from "@/services/backend/documents/types/state";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { backendApi } from "@/services/backend/api";
import { Channel, Order } from "@/services/backend/htz/orders/types/order";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { PositionPatch } from "@/services/backend/htz/position/types/position";

const orderApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    htzOrderCreate: build.mutation<Order, CreateRequest>({
      query: (request) => ({
        url: `/htz/order`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "HtzOrder", id: request.id },
        { type: "HtzOrder", id: "LIST" },
      ],
    }),
    htzOrderAddPosition: build.mutation<Order, AddPositionRequest>({
      query: (request) => ({
        url: `/htz/order/position`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "HtzOrder", id: request.id },
        { type: "HtzOrder", id: "LIST" },
      ],
    }),
    htzOrderPatchPosition: build.mutation<Order, PatchPositionRequest>({
      query: (request) => ({
        url: `/htz/order/position`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "HtzOrder", id: request.id },
        { type: "HtzOrder", id: "LIST" },
      ],
    }),
    htzOrderRemovePosition: build.mutation<Order, RemovePositionRequest>({
      query: (request) => ({
        url: `/htz/order/position`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "HtzOrder", id: request.id },
        { type: "HtzOrder", id: "LIST" },
      ],
    }),
    htzOrderShow: build.query<Order, ShowRequest>({
      query: (request) => ({
        url: `/htz/order`,
        method: "GET",
        params: request,
      }),
      providesTags: providesEntity("HtzOrder"),
    }),
    htzOrderList: build.query<Lst<Order>, ListRequest>({
      query: (request) => ({
        url: `/htz/order/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "HtzOrder"),
    }),
    htzOrderPatch: build.mutation<Order, PatchRequest>({
      query: (request) => ({
        url: `/htz/order`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "HtzOrder", id: request.id },
        { type: "HtzOrder", id: "LIST" },
      ],
    }),
    htzOrderResetReferenceNumber: build.mutation<
      Order,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/htz/order/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "HtzOrder", id: request.id },
        { type: "HtzOrder", id: "LIST" },
      ],
    }),
    htzOrderUpdateState: build.mutation<Order, UpdateStateRequest>({
      query: (request) => ({
        url: `/htz/order/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "HtzOrder", id: request.id },
        { type: "HtzOrder", id: "LIST" },
      ],
    }),
    htzOrderDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `/htz/order`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["HtzOrder"],
    }),
  }),
});

export const {
  useHtzOrderCreateMutation,
  useHtzOrderAddPositionMutation,
  useHtzOrderPatchPositionMutation,
  useHtzOrderRemovePositionMutation,
  useHtzOrderShowQuery,
  useHtzOrderPatchMutation,
  useHtzOrderResetReferenceNumberMutation,
  useHtzOrderListQuery,
  useLazyHtzOrderListQuery,
  useHtzOrderUpdateStateMutation,
  useHtzOrderDeleteMutation,
} = orderApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged, null clears it.
// The positions are changed on their own, the offer by copying it.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
  date?: string | null;
  channel?: Channel | null;
  customerReference?: string;
  internalComment?: string;
  postalAddress?: Address;
  serviceAddress?: Address;
  contact?: Contact;
  representative?: Representative;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export const updateHtzOrderCache = updateCache(orderApi, "htzOrderShow");

/**
 * CreateRequest identifies a new order. Everything else is a default or
 * set with a {@link PatchRequest}.
 */
export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

// AddPositionRequest adds the position `positionId` to the order `id`,
// prefilled with the patch, e.g. from a catalog item.
export interface AddPositionRequest extends PositionPatch {
  id: string;
  positionId: string;
}

// PatchPositionRequest updates the position `positionId` of the order
// `id`.
export interface PatchPositionRequest extends PositionPatch {
  id: string;
  positionId: string;
}

export interface RemovePositionRequest {
  id: string;
  positionId: string;
}
